import {
  Box,
  Flex,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { Util } from '@finley-technologies/finley-types';

import {
  AllBorrowingBaseReportSummaryFields,
  BorrowingBaseSummaryValueType,
  getFormattedValueDifferenceTextAndFormatting,
  verificationRawValueRoundingErrorAllowance,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

type BorrowingBaseSummaryValueDifferenceDefinitionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
export const BorrowingBaseSummaryValueDifferenceDefinitionModal = ({
  isOpen,
  onClose,
}: BorrowingBaseSummaryValueDifferenceDefinitionModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>How should I interpret the differences?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Table mb="16px">
            <Thead>
              <Tr>
                <Th>Color</Th>
                <Th>Definition</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td color={colors.info}>Blue</Td>
                <Td>Borrower is more punitive than Finley Agent.</Td>
              </Tr>
              <Tr>
                <Td color={colors.error}>Red</Td>
                <Td>Finley Agent is more punitive than the Borrower.</Td>
              </Tr>
              <Tr>
                <Td color={colors.error}>
                  <Box>
                    <Icon
                      icon="circle-exclamation"
                      type="regular"
                      color={colors.error}
                    />
                  </Box>
                </Td>
                <Td>
                  Finley Agent is more punitive than the Borrower and the
                  difference is greater than 15% of Borrower’s value.
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type ValueDifferenceGridItemProps = {
  minuend?: Util.Maybe<any>;
  subtrahend?: Util.Maybe<any>;
  valueType: BorrowingBaseSummaryValueType;
  fieldName: AllBorrowingBaseReportSummaryFields;
};
export const ValueDifferenceGridItem = ({
  minuend,
  subtrahend,
  valueType,
  fieldName,
}: ValueDifferenceGridItemProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    valueDifferenceFormattedText,
    valueDifferenceBackgroundColor,
    valueDifferenceTextColor,
    valueDifferenceAlertIcon,
    valueDifferenceInfoIcon,
  } = getFormattedValueDifferenceTextAndFormatting({
    minuend,
    subtrahend,
    valueType,
    fieldName,
  });

  const handleAlertIconOnClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    onOpen();
    // the alert icon could be on an active accordion row
    // we do not want the accordion expansion to toggle when specifically clicking the alert icon
    event.stopPropagation();
  };

  return (
    <GridItem
      colSpan={1}
      alignSelf="center"
      padding="8px 16px"
      backgroundColor={valueDifferenceBackgroundColor}
    >
      <Flex alignItems="center">
        {valueDifferenceAlertIcon && (
          <Box
            onClick={(event) => handleAlertIconOnClick(event)}
            mr="8px"
            cursor="pointer"
          >
            <Icon
              icon="circle-exclamation"
              type="regular"
              color={colors.error}
            />
          </Box>
        )}
        <Text
          fontSize="16px"
          fontWeight={600}
          textAlign="left"
          color={valueDifferenceTextColor}
        >
          {valueDifferenceFormattedText}
        </Text>
        {valueDifferenceInfoIcon && (
          <Box ml="8px" cursor="pointer">
            <Tooltip
              label={`The difference in values is ${verificationRawValueRoundingErrorAllowance} or less`}
            >
              <span>
                <Icon icon="circle-info" type="regular" color={colors.info} />
              </span>
            </Tooltip>
          </Box>
        )}
      </Flex>
      <BorrowingBaseSummaryValueDifferenceDefinitionModal
        isOpen={isOpen}
        onClose={onClose}
      />
    </GridItem>
  );
};
